/**
 * OnboardingShell.jsx — RC 1.44.O1.A unified onboarding chrome.
 *
 * One header + progress treatment shared by both onboarding steps so
 * the wizard reads as a coherent 2-step flow:
 *
 *   Step 1 of 2 — Upload your Source CV   (OnboardingPage)
 *   Step 2 of 2 — Choose your plan        (OnboardingPlanPage)
 *
 * Strict rails:
 *   - PRESENTATIONAL ONLY. No auth, no routing, no API calls.
 *   - No new dependencies.
 *   - Children own their own content max-width and padding.
 *   - Progress bar is purely cosmetic (step / totalSteps).
 *   - All testids preserved from the previous standalone headers
 *     (`onboarding-step-indicator`); new shell-level testid
 *     `onboarding-shell` added for E2E.
 */

import { Progress } from '@/components/ui/progress';
import BrandLogo from '@/components/BrandLogo';

export default function OnboardingShell({
  step,
  totalSteps = 2,
  stepLabel,
  children,
  testid = 'onboarding-shell',
}) {
  // Defensive — coerce out-of-range values so the progress bar can
  // never overflow even if a caller passes bogus props.
  const safeStep   = Math.min(Math.max(Number(step) || 1, 1), totalSteps);
  const safeTotal  = Math.max(Number(totalSteps) || 2, 1);
  const percent    = (safeStep / safeTotal) * 100;

  return (
    <div
      className="min-h-screen bg-ndor-canvas flex flex-col"
      data-testid={testid}
    >
      {/* Shared header — logo + step indicator + progress bar */}
      <header className="border-b border-ndor-edge bg-ndor-surface px-4 py-3 sm:px-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <BrandLogo variant="symbol" size={28} />
            <span className="text-base font-serif-display font-semibold tracking-tight text-ndor-ink">
              NDORFlow
            </span>
          </div>
          <span
            className="text-xs text-ndor-ink-muted font-manrope"
            data-testid="onboarding-step-indicator"
          >
            Step {safeStep} of {safeTotal}
            {stepLabel ? ` · ${stepLabel}` : ''}
          </span>
        </div>
        <div className="max-w-5xl mx-auto mt-2.5">
          <Progress
            value={percent}
            className="h-1"
            data-testid="onboarding-progress-bar"
          />
        </div>
      </header>

      {/* Content surface — children control their own max-width */}
      <main className="flex-1 px-4 py-6 sm:py-10 sm:px-6 pb-24">
        {children}
      </main>
    </div>
  );
}
